import { useEffect, useRef } from "react";
import oditiLogo from "images/oditi-shop-logo.png";

import { processCertificateData } from "../utils/certificate";

export default function CertificatePrint({ cert, onClose, printDate, printRef }) {
  const certificateRef = useRef(null);

  // Disable body scrolling when overlay is open
  useEffect(() => {
    // Store the original overflow value
    const originalOverflow = document.body.style.overflow;
    // Disable scrolling
    document.body.style.overflow = "hidden";

    // Re-enable scrolling when component unmounts
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Process certificate data using shared utility
  const {
    cert: processedCert,
    formattedPrintDate,
    productTitleForUrl,
    qrUrl,
  } = processCertificateData(cert, printDate);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center md:p-6"
      ref={certificateRef}
    >
      {/* backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0 bg-collectors-black/80 backdrop-blur-sm transition-opacity duration-500"
      ></div>

      <div
        className="certificate-content relative bg-collectors-bg w-full max-w-5xl h-auto max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up flex flex-col overflow-hidden"
        style={{ backgroundColor: "#f5f5f5" }}
      >
        {/* close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 p-2 bg-collectors-black text-white hover:bg-red-600 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* certificate details - full width */}
        <div
          ref={printRef}
          className="w-full mt-3 p-8 flex flex-col relative"
          style={{ backgroundColor: "#f5f5f5" }}
        >
          {/* header */}
          <div className="flex justify-between font-mono text-[10px] uppercase tracking-tight -mt-2 mb-4 border-solid border-b-2 border-collectors-black pb-2">
            <p>Certificate of Authenticity</p>
            <p>
              {new Date().toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>
          <div className="border-solid border-b-2 border-collectors-black pt-2 pb-5 mb-6 text-center">
            {/* <span className="bg-collectors-black text-white px-2 py-0.5 font-mono text-xs uppercase -ms-1">
              Certificate of Authenticity
            </span> */}
            <img src={oditiLogo} alt="ODITI Logo" className="w-auto h-3 mb-2" />
            <h1 className="font-fisplay text-4xl font-bold uppercase leading-[0.9] tracking-tighter mt-2 mb-2 -ms-1">
              {processedCert.title}
            </h1>
            <p className="font-mono text-sm uppercase tracking-tight">{processedCert.artist}</p>
          </div>

          {/* specs */}
          <div className="grid grid-cols-2 gap-8 font-mono text-xs uppercase mb-6">
            <div>
              <span className="block text-collectors-gray mb-1 border-solid border-b border-collectors-gray/30 pb-1">
                Medium
              </span>
              <span className="block font-bold text-sm tracking-tight">
                GICLEE PRINT ON MATTE ARCHIVAL PAPER
              </span>
            </div>
            <div>
              <span className="block text-collectors-gray mb-1 border-solid border-b border-collectors-gray/30 pb-1">
                Dimensions
              </span>
              <span className="block font-bold text-sm tracking-tight">
                {processedCert.variant_title.split("/")[0].replaceAll('"', " inches")}
              </span>
            </div>
            <div>
              <span className="block text-collectors-gray mb-1 border-solid border-b border-collectors-gray/30 pb-1">
                Edition
              </span>
              <span className="block font-bold text-sm tracking-tight">
                {processedCert.edition}
              </span>
            </div>
            <div>
              <span className="block text-collectors-gray mb-1 border-solid border-b border-collectors-gray/30 pb-1">
                Print Date
              </span>
              <span className="block font-bold text-sm tracking-tight">{formattedPrintDate}</span>
            </div>
          </div>

          {/* description */}
          <div className="mb-6 mt-2">
            <p className="font-sans text-sm leading-relaxed text-collectors-black border-solid border-l-2 border-oditi-black pl-4">
              This document certifies that the work received is an authentic fine art print by{" "}
              {processedCert.artist} and was published by ODITI. All copyright and further
              reproduction rights are retained by {processedCert.artist}.
            </p>
          </div>

          {/* footer */}
          <div className="grid grid-cols-2 gap-8 font-mono text-xs mb-6 border-solid border-t-2 border-collectors-black pt-6 ps-1">
            {/* artist signature element */}
            <div className="inline-flex flex-col">
              <div className="border-solid border-b border-collectors-gray/30 pb-2 mb-2 text-collectors-gray">
                <span className="block font-medium text-black">{processedCert.artist}</span>
                <span className="block text-xs uppercase tracking-wide">ODITI Collaborator</span>
              </div>
              <span className="mt-3 font-signature text-3xl font-bold tracking-wider capitalize">
                {processedCert.artist.toLowerCase().replace(/[0-9]/g, "").trim()}
              </span>
            </div>
            {/* harry signature element */}
            <div className="inline-flex flex-col">
              <div className="border-solid border-b border-collectors-gray/30 pb-2 mb-2 text-collectors-gray">
                <span className="block font-medium text-black">Harry Beard</span>
                <span className="block text-xs uppercase tracking-wide">Co-founder at ODITI</span>
              </div>
              <span className="mt-3 font-signature text-3xl font-bold tracking-wider">
                Harry Beard
              </span>
              {/* <div className="pt-1 pr-4 mx-auto"> 
                <img 
                  className="h-16" 
                  src="https://cdn.shopify.com/s/files/1/0921/0239/4188/files/harry_1.svg?v=1764183136" 
                  alt="Harry Beard signature" 
                />
              </div> */}
            </div>
          </div>
          <div className="pt-6 border-solid border-t-2 border-collectors-black grid grid-cols-1 gap-4">
            {/* qr link */}
            <a
              href={`https://oditi.com/products/${productTitleForUrl}`}
              target="_blank"
              className="flex group hover:bg-collectors-black hover:cursor-pointer border border-solid border-black gap-4 no-underline items-center p-2 bg-white"
            >
              <div className="w-20 h-20 bg-white border-solid border-collectors-black p-1">
                <img
                  src={qrUrl}
                  alt="Product QR Code"
                  className="w-full h-full object-contain mix-blend-multiply"
                />
              </div>
              <div className="flex flex-col gap-1 text-collectors-black">
                <span className="font-mono text-xs uppercase text-collectors-gray group-hover:text-white">
                  View Product
                </span>
                <span className="font-display group-hover:text-white group-hover:underline text-collectors-black font-bold uppercase text-sm tracking-tight">
                  oditi.com ↗
                </span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
